import { useEffect, useMemo, useState } from 'react'
import { apiClient } from '../../services/api'
import Button from '../../components/ui/Button'
import { ui } from '../../styles/ui'

export default function LlmPromptProfilesPanel({ engines }) {
  const [catalog, setCatalog] = useState(null)
  const [engineId, setEngineId] = useState(engines.find((engine) => engine.active)?.id || '')
  const [operation, setOperation] = useState('receipt_slow')
  const [promptText, setPromptText] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const activeProfile = useMemo(() => catalog?.profiles?.find((profile) => profile.engineId === engineId)
    ?.operations?.find((entry) => entry.operation === operation), [catalog, engineId, operation])
  const refresh = async () => { const response = await apiClient.get('/admin/llm-prompts'); setCatalog(response.data.data) }
  useEffect(() => { refresh().catch(() => setMessage('Prompt profile history is unavailable')) }, [])
  useEffect(() => { setPromptText(activeProfile?.active?.promptText || '') }, [activeProfile])

  const save = async (reset = false) => {
    setBusy(true); setMessage('')
    try {
      const response = await apiClient.put('/admin/llm-prompts', { engineId, operation, reason, ...(reset ? { reset: true } : { promptText }) })
      setMessage(`Saved prompt revision ${response.data.data.revision}`); setReason(''); await refresh()
    } catch (error) { setMessage(error.response?.data?.message || 'Prompt profile update failed') }
    finally { setBusy(false) }
  }
  const restore = async (revision) => {
    setBusy(true); setMessage('')
    try {
      const response = await apiClient.post(`/admin/llm-prompts/${encodeURIComponent(engineId)}/${operation}/${revision}/restore`, { reason })
      setMessage(`Restored as prompt revision ${response.data.data.revision}`); setReason(''); await refresh()
    } catch (error) { setMessage(error.response?.data?.message || 'Prompt restore failed') }
    finally { setBusy(false) }
  }

  return <section className={ui.card} aria-label="Versioned LLM prompt profiles"><div className={ui.cardBody}>
    <h2 className="text-lg font-semibold">Engine and operation prompt profiles</h2>
    <p className={ui.subtitle}>Explicit prompts are immutable revisions. Reset returns to that engine&apos;s own legacy/default prompt; prompts are never copied across providers.</p>
    <div className="grid gap-3 md:grid-cols-2">
      <label className={ui.label}>Engine<select className={'mt-1 ' + ui.select} value={engineId} onChange={(event) => setEngineId(event.target.value)}>
        {engines.map((engine) => <option key={engine.id} value={engine.id}>{engine.name || engine.id}</option>)}</select></label>
      <label className={ui.label}>Operation<select className={'mt-1 ' + ui.select} value={operation} onChange={(event) => setOperation(event.target.value)}>
        {(catalog?.operations || []).map((value) => <option key={value} value={value}>{value}</option>)}</select></label>
    </div>
    <p className="text-sm">Current source: {activeProfile?.active?.mode === 'explicit' ? `revision ${activeProfile.active.revision}` : 'engine legacy/default'}</p>
    <label className={ui.label}>Prompt<textarea className={'mt-1 min-h-48 w-full ' + ui.input} value={promptText} onChange={(event) => setPromptText(event.target.value)} /></label>
    <label className={ui.label}>Audit reason<input className={'mt-1 ' + ui.input} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
    <div className="flex gap-2"><Button type="button" disabled={busy || reason.trim().length < 8 || !engineId} onClick={() => save(false)}>Save prompt revision</Button>
      <Button type="button" variant="ghost" disabled={busy || reason.trim().length < 8 || !engineId} onClick={() => save(true)}>Reset to engine fallback</Button></div>
    {message && <p role="status" className="mt-2 text-sm">{message}</p>}
    <div className="mt-4"><h3 className="font-medium">Prompt history</h3>{(activeProfile?.history || []).map((entry) => <p key={entry.id} className="text-sm">#{entry.revision} · {entry.mode} · {entry.promptSha256.slice(0, 12)}
      {!entry.active && <button className="ml-2 underline" disabled={busy || reason.trim().length < 8} onClick={() => restore(entry.revision)}>Restore as new revision</button>}</p>)}</div>
  </div></section>
}
